<template>
  <section class="bg-gray50 pt-0">
    <div class="row" style="margin-top:-200px;">
      <div class="column twelve">
        <div class="relative flex justify-center">
          <div class="ml-30" v-if="logoutUrl">
            <a :href="logoutUrl" class="outlined-button text-center text-blue900 border-white-30">
              <span class="outlined-button-content text-white">Logout</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <brokerage-search
        :query="query"
        :ownership-items="ownershipItems"
        :type-items="typeItems"
        :sqft-range="sqftRange"
        @update-query="setQuery"
        @toggle-refinement="toggleRefinement"
        @update-sqft-range="setSqftRange"
    />

    <brokerage-container
        :listings="listings"
        :loading="loading"
        :matches="matches"
        :view="view"
        :sort-option="sortOption"
        :has-active-filters="hasActiveFilters"
        @update:view="view = $event"
        @update:sort-option="setSortOption"
        @reset-filters="resetFilters"
    />

    <div
        v-if="loading"
        class="text-gray200 text-4xl font-bold py-100 flex items-center justify-center"
    >
      Loading, please wait...
    </div>

    <div
        v-if="!loading && !listings.length"
        class="text-gray200 text-4xl font-bold py-100 flex items-center justify-center"
    >
      No listings found, please try another search.
    </div>
  </section>
</template>

<script>
import BrokerageSearch from './brokerage-search.vue';
import BrokerageContainer from './brokerage-container.vue';
import algoliasearch from 'algoliasearch';

const algoliaApplicationId = process.env.ALGOLIA_APPLICATION_ID;
const algoliaSearchKey = process.env.ALGOLIA_SEARCH_APIKEY;
const scoutIndexPrefix = process.env.SCOUT_INDEX_PREFIX || '';
const searchClient = algoliasearch(algoliaApplicationId, algoliaSearchKey);
const facetAttributes = {
  ownership: 'realEstate_label',
  type: 'typeOfVenue_label',
};
const sqftRanges = {
  under5000: ['squareFootage <= 5000'],
  '5000-10000': ['squareFootage >= 5000', 'squareFootage <= 10000'],
  over10000: ['squareFootage >= 10000'],
};

// Owns the brokerage search state and Algolia requests for /brokerage-listings.
export default {
  components: {
    BrokerageSearch,
    BrokerageContainer,
  },
  props: {
    windowSize: { type: String, default: 'md' },
    logoutUrl: { type: String, default: '' },
  },
  data() {
    return {
      query: '',
      listings: [],
      loading: false,
      matches: 0,
      facets: {},
      selectedOwnership: [],
      selectedTypes: [],
      sqftRange: 'any',
      sortOption: 'relevance',
      view: 'list',
      searchTimer: null,
      requestId: 0,
    };
  },
  computed: {
    indexName() {
      return scoutIndexPrefix + 'BrokerageListings';
    },
    ownershipItems() {
      return this.getFacetItems(facetAttributes.ownership, this.selectedOwnership);
    },
    typeItems() {
      return this.getFacetItems(facetAttributes.type, this.selectedTypes);
    },
    hasActiveFilters() {
      return (
          this.query ||
          this.selectedOwnership.length ||
          this.selectedTypes.length ||
          this.sqftRange !== 'any'
      ) ? true : false;
    },
  },
  mounted() {
    this.getListings();
  },
  beforeUnmount() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  },
  methods: {
    setQuery(value) {
      this.query = value;
      this.queueSearch();
    },
    setSortOption(value) {
      this.sortOption = value;
      this.listings = this.sortListings(this.listings);
    },
    setSqftRange(value) {
      this.sqftRange = value || 'any';
      this.getListings();
    },
    toggleRefinement(group, value) {
      const selected =
          group === 'ownership' ? this.selectedOwnership : this.selectedTypes;
      const index = selected.indexOf(value);

      if (index >= 0) {
        selected.splice(index, 1);
      } else {
        selected.push(value);
      }

      this.getListings();
    },
    resetFilters() {
      this.query = '';
      this.selectedOwnership = [];
      this.selectedTypes = [];
      this.sqftRange = 'any';
      this.getListings();
    },
    queueSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      this.searchTimer = setTimeout(() => {
        this.getListings();
      }, 250);
    },
    getFacetItems(attribute, selectedValues) {
      const counts = this.facets[attribute] || {};
      const values = Object.keys(counts);

      selectedValues.forEach(value => {
        if (!values.includes(value)) {
          values.push(value);
        }
      });

      return values
          .sort((a, b) => a.localeCompare(b))
          .map(value => ({
            label: value,
            value,
            count: counts[value] || 0,
            isRefined: selectedValues.includes(value),
          }));
    },
    getFacetFilters() {
      const filters = [];

      if (this.selectedOwnership.length) {
        filters.push(
            this.selectedOwnership.map(value => `${facetAttributes.ownership}:${value}`)
        );
      }

      if (this.selectedTypes.length) {
        filters.push(this.selectedTypes.map(value => `${facetAttributes.type}:${value}`));
      }

      return filters;
    },
    getNumericFilters() {
      return sqftRanges[this.sqftRange] || [];
    },
    getListings() {
      const currentRequestId = ++this.requestId;
      const index = searchClient.initIndex(this.indexName);

      this.loading = true;

      index.search(
          this.query,
          {
            hitsPerPage: 500,
            facets: Object.values(facetAttributes),
            maxValuesPerFacet: 100,
            facetFilters: this.getFacetFilters(),
            numericFilters: this.getNumericFilters(),
          },
          (error, response = {}) => {
            if (currentRequestId !== this.requestId) {
              return;
            }

            if (error) {
              console.error(error);
              this.listings = [];
              this.matches = 0;
              this.facets = {};
              this.loading = false;
              return;
            }

            this.listings = this.sortListings(response.hits || []);
            this.matches = response.nbHits || 0;
            this.facets = response.facets || {};
            this.loading = false;
          }
      );
    },
    sortListings(listings) {
      if (this.sortOption === 'price_asc') {
        return this.sortByNumber(listings, 'price', 'asc');
      }

      if (this.sortOption === 'price_desc') {
        return this.sortByNumber(listings, 'price', 'desc');
      }

      if (this.sortOption === 'sqft_desc') {
        return this.sortByNumber(listings, 'squareFootage', 'desc');
      }

      return listings;
    },
    sortByNumber(listings, key, direction) {
      return [...listings].sort((a, b) => {
        const aValue = Number(a[key]);
        const bValue = Number(b[key]);
        const aHasValue = Number.isFinite(aValue);
        const bHasValue = Number.isFinite(bValue);

        if (!aHasValue && !bHasValue) {
          return 0;
        }

        if (!aHasValue) {
          return 1;
        }

        if (!bHasValue) {
          return -1;
        }

        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      });
    },
  },
};
</script>
